import React, { useState } from "react"
import { css } from "@emotion/core"

import copy from "./copy"

const FORM_NAME = "sqs-support-request"

const initialValues = {
  nom: "",
  prenom: "",
  situation: "",
  methodeContact: "",
  courriel: "",
  telephone: "",
  autorisationMessageVocal: "",
  detailsSituation: "",
  consentement: false,
  champAntiRobot: "",
}

const sectionStyle = css`
  margin-top: 32px;
  padding: 30px;
  border-radius: 4px;
  box-shadow: 0 3px 6px -2px rgba(0, 0, 0, 0.3);
  background: #fff;

  @media (max-width: 768px) {
    padding: 22px 18px;
  }
`

const titleStyle = css`
  margin: 0 0 24px;
  font-size: 26px;
  line-height: 1.35;
`

const gridStyle = css`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

const fieldStyle = css`
  margin-bottom: 20px;

  label,
  legend {
    display: block;
    margin-bottom: 7px;
    font-weight: 700;
  }

  input[type="text"],
  input[type="email"],
  input[type="tel"],
  select,
  textarea {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #767676;
    border-radius: 3px;
    background: #fff;
    color: #222;
    font: inherit;
  }

  textarea {
    min-height: 130px;
    resize: vertical;
  }

  input:focus,
  select:focus,
  textarea:focus {
    outline: 3px solid rgba(31, 127, 200, 0.3);
    outline-offset: 1px;
    border-color: #1f7fc8;
  }
`

const fieldsetStyle = css`
  margin: 0 0 20px;
  padding: 0;
  border: 0;
`

const choiceStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;

  label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    font-weight: 400;
  }
`

const situationChoiceStyle = css`
  display: flex;
  flex-direction: column;
  gap: 12px;

  label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    font-weight: 400;
  }
`

const errorStyle = css`
  display: block;
  margin-top: 6px;
  color: #a20d2c;
  font-weight: 700;
`

const consentStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 24px 0;

  input {
    flex: 0 0 auto;
    margin-top: 4px;
  }
`

const buttonStyle = css`
  min-width: 180px;
  padding: 12px 20px;
  border: 0;
  border-radius: 999px;
  background: #1f7fc8;
  color: #fff;
  font: inherit;
  font-weight: 700;
  cursor: pointer;

  &:hover,
  &:focus {
    background: #155d91;
  }

  &:focus {
    outline: 3px solid rgba(31, 127, 200, 0.35);
    outline-offset: 2px;
  }

  &:disabled {
    cursor: wait;
    opacity: 0.65;
  }
`

const statusStyle = css`
  margin: 18px 0 0;
  padding: 12px 14px;
  border-left: 4px solid #1f7fc8;
  background: #eef7fd;
`

const honeypotStyle = css`
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
`

const requiredLabel = (label, required) => `${label}${required ? " *" : ""}`

const validate = (values, messages) => {
  const errors = {}

  if (!values.nom.trim()) errors.nom = messages.required
  if (!values.prenom.trim()) errors.prenom = messages.required
  if (!values.situation) errors.situation = messages.required
  if (!values.methodeContact) errors.methodeContact = messages.required

  if (values.methodeContact === "courriel") {
    if (!values.courriel.trim()) errors.courriel = messages.required
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.courriel.trim()))
      errors.courriel = messages.invalidEmail
  }

  if (values.methodeContact === "telephone") {
    if (!values.telephone.trim()) errors.telephone = messages.required
    if (!values.autorisationMessageVocal)
      errors.autorisationMessageVocal = messages.required
  }

  if (!values.consentement) errors.consentement = messages.required

  return errors
}

const FieldError = ({ id, message }) =>
  message ? (
    <span id={id} css={errorStyle}>
      {message}
    </span>
  ) : null

const SituationLabel = ({ copy }) => (
  <span>
    {copy.before}
    <strong>{copy.emphasis}</strong>
    {copy.after}
  </span>
)

const addSubmissionValue = (params, name, value) => {
  if (value !== "" && value !== false && value !== null && value !== undefined)
    params.append(name, String(value))
}

export const SupportRequestForm = ({
  stateProps,
  htmlProps,
  isCanvas = false,
}) => {
  const [pageState] = stateProps.pageContext
  const isEnglish = pageState.lang === "en-ca"
  const messages = isEnglish ? copy.en : copy.fr
  const [values, setValues] = useState(initialValues)
  const [errors, setErrors] = useState({})
  const [status, setStatus] = useState("idle")

  const updateValue = event => {
    const { name, type, checked, value } = event.target
    const stateName = name

    setValues(current => {
      const next = {
        ...current,
        [stateName]: type === "checkbox" ? checked : value,
      }

      if (stateName === "methodeContact" && value === "courriel") {
        next.telephone = ""
        next.autorisationMessageVocal = ""
      } else if (stateName === "methodeContact" && value === "telephone") {
        next.courriel = ""
      }

      return next
    })

    if (errors[stateName])
      setErrors(current => {
        const next = { ...current }
        delete next[stateName]
        return next
      })

    if (status !== "idle") setStatus("idle")
  }

  const submit = async event => {
    event.preventDefault()
    if (isCanvas) return

    const nextErrors = validate(values, messages)
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length) {
      setStatus("invalid")
      return
    }

    setStatus("submitting")

    const params = new URLSearchParams()
    addSubmissionValue(params, "form-name", FORM_NAME)
    addSubmissionValue(params, "nom", values.nom.trim())
    addSubmissionValue(params, "prenom", values.prenom.trim())
    addSubmissionValue(params, "situation", values.situation)
    addSubmissionValue(params, "methodeContact", values.methodeContact)
    addSubmissionValue(params, "courriel", values.courriel.trim())
    addSubmissionValue(params, "telephone", values.telephone.trim())
    addSubmissionValue(
      params,
      "autorisationMessageVocal",
      values.autorisationMessageVocal
    )
    addSubmissionValue(
      params,
      "detailsSituation",
      values.detailsSituation.trim()
    )
    addSubmissionValue(
      params,
      "consentement",
      values.consentement ? "accepte" : ""
    )
    addSubmissionValue(params, "champAntiRobot", values.champAntiRobot)
    addSubmissionValue(params, "langue", isEnglish ? "en" : "fr")
    addSubmissionValue(params, "publicQuestionnaire", pageState.type || "")
    addSubmissionValue(
      params,
      "pageSource",
      typeof window === "undefined" ? "" : window.location.pathname
    )

    try {
      const response = await fetch("/", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params.toString(),
      })

      if (!response.ok)
        throw new Error(`Form submission failed: ${response.status}`)

      setValues(initialValues)
      setErrors({})
      setStatus("success")
    } catch (error) {
      setStatus("error")
    }
  }

  const describedBy = name => (errors[name] ? `${name}-error` : undefined)

  return (
    <section {...htmlProps} css={sectionStyle}>
      <h2 css={titleStyle}>{messages.title}</h2>
      <form
        name={FORM_NAME}
        method="POST"
        data-netlify={isEnglish ? undefined : "true"}
        data-netlify-honeypot={isEnglish ? undefined : "champAntiRobot"}
        noValidate
        onSubmit={submit}
      >
        <input type="hidden" name="form-name" value={FORM_NAME} />
        <input type="hidden" name="langue" value="" />
        <input type="hidden" name="publicQuestionnaire" value="" />
        <input type="hidden" name="pageSource" value="" />
        <p css={honeypotStyle}>
          <label>
            Do not fill this out:
            <input
              name="champAntiRobot"
              value={values.champAntiRobot}
              onChange={updateValue}
              tabIndex="-1"
              autoComplete="off"
            />
          </label>
        </p>

        <div css={gridStyle}>
          <div css={fieldStyle}>
            <label htmlFor="nom">
              {requiredLabel(messages.lastName, true)}
            </label>
            <input
              id="nom"
              name="nom"
              type="text"
              autoComplete="family-name"
              value={values.nom}
              onChange={updateValue}
              aria-invalid={Boolean(errors.nom)}
              aria-describedby={describedBy("nom")}
            />
            <FieldError id="nom-error" message={errors.nom} />
          </div>

          <div css={fieldStyle}>
            <label htmlFor="prenom">
              {requiredLabel(messages.firstName, true)}
            </label>
            <input
              id="prenom"
              name="prenom"
              type="text"
              autoComplete="given-name"
              value={values.prenom}
              onChange={updateValue}
              aria-invalid={Boolean(errors.prenom)}
              aria-describedby={describedBy("prenom")}
            />
            <FieldError id="prenom-error" message={errors.prenom} />
          </div>
        </div>

        <fieldset
          css={fieldsetStyle}
          aria-invalid={Boolean(errors.situation)}
          aria-describedby={describedBy("situation")}
        >
          <legend>{requiredLabel(messages.situation, true)}</legend>
          <div css={situationChoiceStyle}>
            <label>
              <input
                type="radio"
                name="situation"
                value="ma-sante-mentale"
                checked={values.situation === "ma-sante-mentale"}
                onChange={updateValue}
              />
              <SituationLabel copy={messages.situationSelf} />
            </label>
            <label>
              <input
                type="radio"
                name="situation"
                value="sante-mentale-proche"
                checked={values.situation === "sante-mentale-proche"}
                onChange={updateValue}
              />
              <SituationLabel copy={messages.situationLovedOne} />
            </label>
          </div>
          <FieldError id="situation-error" message={errors.situation} />
        </fieldset>

        <fieldset
          css={fieldsetStyle}
          aria-invalid={Boolean(errors.methodeContact)}
          aria-describedby={describedBy("methodeContact")}
        >
          <legend>{requiredLabel(messages.contactMethod, true)}</legend>
          <div css={choiceStyle}>
            <label>
              <input
                type="radio"
                name="methodeContact"
                value="courriel"
                checked={values.methodeContact === "courriel"}
                onChange={updateValue}
              />
              {messages.email}
            </label>
            <label>
              <input
                type="radio"
                name="methodeContact"
                value="telephone"
                checked={values.methodeContact === "telephone"}
                onChange={updateValue}
              />
              {messages.phone}
            </label>
          </div>
          <FieldError
            id="methodeContact-error"
            message={errors.methodeContact}
          />
        </fieldset>

        <div css={fieldStyle} hidden={values.methodeContact !== "courriel"}>
          <label htmlFor="courriel">
            {requiredLabel(
              messages.email,
              values.methodeContact === "courriel"
            )}
          </label>
          <input
            id="courriel"
            name="courriel"
            type="email"
            autoComplete="email"
            value={values.courriel}
            onChange={updateValue}
            disabled={values.methodeContact !== "courriel"}
            aria-invalid={Boolean(errors.courriel)}
            aria-describedby={describedBy("courriel")}
          />
          <FieldError id="courriel-error" message={errors.courriel} />
        </div>

        <div css={fieldStyle} hidden={values.methodeContact !== "telephone"}>
          <label htmlFor="telephone">
            {requiredLabel(
              messages.phone,
              values.methodeContact === "telephone"
            )}
          </label>
          <input
            id="telephone"
            name="telephone"
            type="tel"
            autoComplete="tel"
            value={values.telephone}
            onChange={updateValue}
            disabled={values.methodeContact !== "telephone"}
            aria-invalid={Boolean(errors.telephone)}
            aria-describedby={describedBy("telephone")}
          />
          <FieldError id="telephone-error" message={errors.telephone} />
        </div>

        <fieldset
          css={fieldsetStyle}
          hidden={values.methodeContact !== "telephone"}
          disabled={values.methodeContact !== "telephone"}
          aria-invalid={Boolean(errors.autorisationMessageVocal)}
          aria-describedby={describedBy("autorisationMessageVocal")}
        >
          <legend>{requiredLabel(messages.voicemailConsent, true)}</legend>
          <div css={choiceStyle}>
            <label>
              <input
                type="radio"
                name="autorisationMessageVocal"
                value="oui"
                checked={values.autorisationMessageVocal === "oui"}
                onChange={updateValue}
              />
              {messages.yes}
            </label>
            <label>
              <input
                type="radio"
                name="autorisationMessageVocal"
                value="non"
                checked={values.autorisationMessageVocal === "non"}
                onChange={updateValue}
              />
              {messages.no}
            </label>
          </div>
          <FieldError
            id="autorisationMessageVocal-error"
            message={errors.autorisationMessageVocal}
          />
        </fieldset>

        <div css={fieldStyle}>
          <label htmlFor="details">{messages.details}</label>
          <textarea
            id="details"
            name="detailsSituation"
            value={values.detailsSituation}
            onChange={updateValue}
          />
        </div>

        <div css={consentStyle}>
          <input
            id="consentement"
            name="consentement"
            type="checkbox"
            value="accepte"
            checked={values.consentement}
            onChange={updateValue}
            aria-invalid={Boolean(errors.consentement)}
            aria-describedby={describedBy("consentement")}
          />
          <div>
            <label htmlFor="consentement">
              {requiredLabel(messages.consent, true)}
            </label>
            <FieldError id="consentement-error" message={errors.consentement} />
          </div>
        </div>

        <button
          css={buttonStyle}
          type="submit"
          disabled={status === "submitting"}
        >
          {status === "submitting" ? messages.submitting : messages.submit}
        </button>

        {status === "invalid" && (
          <p css={statusStyle} role="alert">
            {messages.invalid}
          </p>
        )}
        {status === "success" && (
          <p css={statusStyle} role="status">
            {messages.success}
          </p>
        )}
        {status === "error" && (
          <p css={statusStyle} role="alert">
            {messages.failure}
          </p>
        )}
      </form>
    </section>
  )
}

SupportRequestForm.displayName = "SupportRequestForm"

export const SupportRequestFormCanvas = props => (
  <SupportRequestForm {...props} isCanvas />
)
SupportRequestFormCanvas.displayName = "SupportRequestFormCanvas"
